import {
  Component,
  forwardRef,
  Input,
  type TrackByFunction,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

import {
  type DataLevelItem,
  type LevelItem,
  type MultiLevelData,
} from './level-item.interface';
import { MultilevelToggleLineComponent } from './multilevel-toggle-line/multilevel-toggle-line.component';
import {
  convertDataLevelItemsToLevelItems,
  convertLevelItemsToDataLevelItems,
  trackByDefault,
} from './multilevel-toggle.helpers';

export const MULTILEVEL_TOGGLE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultilevelToggleComponent),
  multi: true,
};

@Component({
  selector: 'multilevel-toggle',
  templateUrl: './multilevel-toggle.component.html',
  styleUrls: ['./multilevel-toggle.component.scss'],
  providers: [MULTILEVEL_TOGGLE_VALUE_ACCESSOR],
  imports: [MultilevelToggleLineComponent],
})
export class MultilevelToggleComponent implements ControlValueAccessor {
  public levelRows: LevelItem[];
  public trackByFn: TrackByFunction<LevelItem> = trackByDefault;

  @Input()
  public border: boolean = false;

  @Input()
  public spaceTop: boolean = false;

  @Input()
  public withPaddingBottom: boolean = false;

  public registerOnChange(fn: (value: { data: DataLevelItem[] }) => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public writeValue(multiLevelData: MultiLevelData): void {
    this.levelRows = convertDataLevelItemsToLevelItems(multiLevelData.data);
  }

  public onChange(item: LevelItem): void {
    // item is provided if an update have been made
    if (item && item.id) {
      // update levelitem before bubulling
      this.levelRows = this.levelRows.map(row => this._updateItem(row, item));
    }

    this._onModelChange({
      data: convertLevelItemsToDataLevelItems(this.levelRows),
    });
  }

  /**
   * Check the levelRow and its children to update item with id item.id
   * Return the whole levelRow, with row updated if found
   *
   * @param levelRow the current levelRow to check
   * @param item the item to replace
   */
  private _updateItem(levelRow: LevelItem, item: LevelItem): LevelItem {
    if (levelRow.id === item.id) {
      levelRow.data = item.data;
      levelRow.children = item.children;

      return levelRow;
    }
    levelRow.children = levelRow.children.map(row =>
      this._updateItem(row, item),
    );

    return levelRow;
  }

  private _onModelChange: (value: { data: DataLevelItem[] }) => any = () => {};

  protected _onTouched: () => any = () => {};
}
